import "./polyfills/crypto-randomuuid";

import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { PostHogProvider } from "posthog-js/react";
import { StrictMode } from "react";
import { Async } from "react-async";
import * as ReactDOM from "react-dom/client";
import { RouterProvider } from "react-router/dom";

import { ConnectionFailurePage } from "@/components/Error/ConnectionFailurePage";
import { initializeCookieBot } from "@/external/cookiebot";
import { initializeGoogleAnalytics } from "@/external/g-analytics";
import type { PostHogSetup } from "@/external/posthog";
import { getPostHogConfig } from "@/external/posthog";
import { ApplicationLoadingIndicator, StartupLoading } from "@/providers/StartupLoading";

import { router } from "./App";
import { createQueryClient } from "./helpers/query-client";

const queryClient = createQueryClient();

let postHog: PostHogSetup | undefined = undefined;

async function initialize() {
  // Cookie bot must go first to potentially block cookies
  await initializeCookieBot(() => {
    initializeGoogleAnalytics();
  });

  postHog = getPostHogConfig();

  if (!("container" in document.documentElement.style)) {
    await import("container-query-polyfill");
  }

  if (import.meta.env.VITE_MOCK_API_IN_DEVELOPMENT === "true") {
    // imported dynamically to avoid being included in production
    console.warn("Mocks enabled");
    const { worker } = await import("./helpers/mocks/server");
    await worker.start({ onUnhandledRequest: "bypass", quiet: true });
  }
}

const initializePromise = initialize();

const rootElement = document.getElementById("root")!;
const root = ReactDOM.createRoot(rootElement);

const PostHogWrapper = ({ children }: { children: React.ReactNode }) =>
  postHog ? (
    <PostHogProvider options={postHog.options} apiKey={postHog.apiKey}>
      {children}
    </PostHogProvider>
  ) : (
    children
  );

root.render(
  <StrictMode>
    <ApplicationLoadingIndicator />
    <Sentry.ErrorBoundary fallback={<ConnectionFailurePage />}>
      <Async promise={initializePromise}>
        <Async.Pending>
          <StartupLoading />
        </Async.Pending>
        <Async.Rejected>
          <ConnectionFailurePage />
        </Async.Rejected>
        <Async.Fulfilled>
          <PostHogWrapper>
            <QueryClientProvider client={queryClient}>
              <RouterProvider router={router} />
              {import.meta.env.VITE_REACT_QUERY_DEVTOOLS === "true" && <ReactQueryDevtools initialIsOpen={false} />}
            </QueryClientProvider>
          </PostHogWrapper>
        </Async.Fulfilled>
      </Async>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
